import { api } from './api-client';

/**
 * The café's cast, as `/v1/admin/characters` serves it.
 *
 * Declared here rather than imported from the API — ADR 0001 duplicates types
 * between the API and its clients rather than sharing them through a workspace.
 * The shape has to keep matching `api/src/characters/`.
 */
export interface Character {
  id: string;
  /** What the briefing card and the conversation header call them. */
  name: string;
  /** One letter, for the avatar. Stands in until there is character art. */
  initial: string;
  /** How they are, read before meeting them. */
  blurb: string;
  /**
   * How much they will carry the conversation. Written for the model, never
   * shown to a learner — telling somebody how hard the next ninety seconds are
   * about to be is the thing the whole act avoids saying.
   */
  tolerance: string;
  /** Act III's "a character to meet, by name", and the reason it gives. */
  nextUp: string;
  nextUpReason: string;
  /** Lower comes first. Null is unranked and sorts to the back. */
  displayOrder: number | null;
  /** One per language somebody has authored. Empty is common and visible. */
  scenes: Scene[];
  createdAt: string;
  updatedAt: string;
}

/** One character in one language. */
export interface Scene {
  id: string;
  /** As `languages.code` spells it. */
  languageCode: string;
  /** Where this happens, in the learner's own language. */
  setting: string;
  /** What the character is after. Sits where a header subtitle would. */
  wants: string;
  /** Usted or tú, vous or tu. Written for the model. */
  register: string;
  /** What they say into twenty seconds of nothing, in the target language. */
  silencePrompt: string;
  silencePromptGloss: string;
  /** How it ends when the character got what they wanted. */
  closing: string;
  closingGloss: string;
  /**
   * The voice this character is heard in, here — or null to fall back to the
   * language's default. See ADR 0013.
   *
   * On the scene rather than the character for the reason the rest of this
   * shape exists: a character is language-independent and a scene is not.
   * `es-ES-Neural2-F` is not a Portuguese voice.
   */
  voiceName: string | null;
  /** Exactly two. The model writes everything after them. */
  turns: SceneTurn[];
}

/** One authored opening turn. */
export interface SceneTurn {
  ordinal: number;
  /** What the character says, in the target language. */
  line: string;
  /** What it means. Revealed by "what did they say?". */
  gloss: string;
  /** Exactly three. A beginner has to be able to finish by tapping alone. */
  suggestions: string[];
  /** What "say it for me" sends. Allowed to be the politer answer. */
  sayItForMe: string;
  /** What "give me a hint" shows, in the learner's own language. */
  hint: string;
}

/** What the add and edit forms produce. */
export interface CharacterDraft {
  name: string;
  initial: string;
  blurb: string;
  tolerance: string;
  nextUp: string;
  nextUpReason: string;
  displayOrder: number | null;
}

/** What the scene editor produces. Sent whole; it replaces what was stored. */
export interface SceneDraft {
  languageCode: string;
  setting: string;
  wants: string;
  register: string;
  silencePrompt: string;
  silencePromptGloss: string;
  closing: string;
  closingGloss: string;
  voiceName: string | null;
  turns: SceneTurn[];
}

/**
 * How many characters the briefing offers.
 *
 * Mirrored from the API, which is the only side that enforces it. Shown on this
 * screen so a third character does not look broken: it is authored and stored,
 * it is simply not one of the two anybody is offered.
 */
export const OFFERED = 2;

/** How many turns a scene opens with, and how many replies each one offers. */
export const OPENING_TURNS = 2;
export const SUGGESTIONS = 3;

export async function listCharacters(): Promise<Character[]> {
  const { characters } = await api.get<{ characters: Character[] }>(
    '/admin/characters',
  );
  return characters;
}

export async function createCharacter(
  draft: CharacterDraft,
): Promise<Character> {
  const { character } = await api.post<{ character: Character }>(
    '/admin/characters',
    draft,
  );
  return character;
}

export async function updateCharacter(
  id: string,
  draft: CharacterDraft,
): Promise<Character> {
  const { character } = await api.patch<{ character: Character }>(
    `/admin/characters/${id}`,
    draft,
  );
  return character;
}

export function deleteCharacter(id: string): Promise<void> {
  return api.del(`/admin/characters/${id}`);
}

export async function saveScene(
  characterId: string,
  draft: SceneDraft,
): Promise<Scene> {
  const { scene } = await api.put<{ scene: Scene }>(
    `/admin/characters/${characterId}/scenes`,
    draft,
  );
  return scene;
}

export function deleteScene(
  characterId: string,
  languageCode: string,
): Promise<void> {
  return api.del(`/admin/characters/${characterId}/scenes/${languageCode}`);
}

/** An empty opening turn, for a scene nobody has written yet. */
export function blankTurn(ordinal: number): SceneTurn {
  return {
    ordinal,
    line: '',
    gloss: '',
    suggestions: Array.from({ length: SUGGESTIONS }, () => ''),
    sayItForMe: '',
    hint: '',
  };
}

/** An empty scene in one language. */
export function blankScene(languageCode: string): SceneDraft {
  return {
    languageCode,
    setting: '',
    wants: '',
    register: '',
    silencePrompt: '',
    silencePromptGloss: '',
    closing: '',
    closingGloss: '',
    // Null, not the language's voice copied in. A scene that starts by
    // overriding the default cannot fall back to it later without somebody
    // knowing to clear the box.
    voiceName: null,
    turns: Array.from({ length: OPENING_TURNS }, (_, index) => blankTurn(index)),
  };
}
